import React, { useState, useRef, useEffect } from "react";
import { toast } from "react-toastify";
import { useSelector } from "react-redux";
import {
  Box,
  styled,
  Button,
  TextField,
  CircularProgress,
} from "@mui/material";
import { esignToolApi } from "../../services/esignApi";

// Styled components for custom appearance
const OtpInput = styled(TextField)({
  borderRadius: "10px",
  backgroundColor: "#fff",
  borderColor: "#C0C0C0",
  borderWidth: "5px",
  margin: "0 6px",
  "& .MuiOutlinedInput-root": {
    borderRadius: "10px",
    height: "46px",
    width: "46px",
    padding: 0,
    textAlign: "center",
    borderColor: "#C0C0C0",
    backgroundColor: "none",
    "&:hover fieldset": {
      borderColor: "#C0C0C0",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#C0C0C0",
    },
  },
  "& input": {
    textAlign: "center",
    fontSize: "18px",
  },
  "& .MuiOutlinedInput-input": {
    padding: "14px 0",
  },
  "& .MuiFormHelperText-root": {
    color: "#FF6666",
  },
});

const ResendButton = styled(Button)({
  textTransform: "none",
  fontWeight: 500,
  color: "#185CB7",
  backgroundColor: "transparent",
  border: "1px solid #e0e0e0",
  borderRadius: "10px",
  padding: "8px 20px",
  minWidth: "120px",
  "&:hover": {
    backgroundColor: "rgba(25, 118, 210, 0.04)",
  },
});

const VerifyButton = styled(Button)({
  mt: 2,
  mr: 2,
  height: 40,
  color: "#fff",
  width: "155px",
  fontSize: "16px",
  boxShadow: "none",
  fontWeight: 500,
  borderRadius: "10px",
  background: "#185CB7",
  textTransform: "none",
  "&:hover": { background: "#185CB7" },
});

const OTPSection = ({ setCurrentStep }) => {
  const inputRefs = useRef([]);
  const [timer, setTimer] = useState(0);
  const [loading, setLoading] = useState(false);
  const [otp, setOtp] = useState(new Array(6).fill(""));
  const [resendLoading, setResendLoading] = useState(false);
  const { profileData } = useSelector((state) => state.profile);

  // Handle verify button click
  const handleOTPVerify = async () => {
    setLoading(true);
    try {
      const response = await esignToolApi.verifyOtp(
        profileData?.company_email,
        otp.join("")
      );
      console.log(response);
      setCurrentStep("verified");
    } catch (error) {
      toast.error("Incorrect OTP. Please enter a valid one.");
    } finally {
      setLoading(false);
    }
  };

  // Handle OTP input changes
  const handleChange = (index, e) => {
    const value = e.target.value;

    // Only allow numbers
    if (!/^[0-9]*$/.test(value)) return;

    const newOtp = [...otp];
    // Take only the last character if multiple are pasted or entered
    newOtp[index] = value.substring(value.length - 1);
    setOtp(newOtp);

    // Move to next input if current field is filled
    if (value && index < 5) {
      inputRefs.current[index + 1].focus();
    }
  };

  // Handle key events for backspace and navigation
  const handleKeyDown = (index, e) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      // Move to previous input on backspace if current is empty
      inputRefs.current[index - 1].focus();
    } else if (e.key === "ArrowLeft" && index > 0) {
      // Move left on arrow key
      inputRefs.current[index - 1].focus();
    } else if (e.key === "ArrowRight" && index < 3) {
      // Move right on arrow key
      inputRefs.current[index + 1].focus();
    }
  };

  // Handle paste event for the entire OTP
  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text/plain").trim();

    // Only proceed if pasted content is all digits
    if (!/^\d+$/.test(pastedData)) return;

    const newOtp = [...otp];
    for (let i = 0; i < Math.min(pastedData.length, 6); i++) {
      newOtp[i] = pastedData[i];
    }
    setOtp(newOtp);

    // Focus the appropriate field based on paste length
    if (pastedData.length < 6) {
      inputRefs.current[Math.min(pastedData.length, 3)].focus();
    }
  };

  // Check if OTP is complete
  const isOtpComplete = otp.every((digit) => digit !== "");

  // Handle resend OTP
  const handleResend = async () => {
    setResendLoading(true);
    try {
      const response = await esignToolApi.sendOtpForgotPassword(
        profileData?.company_email
      );
      setTimer(30);
      toast.success("OTP has been resent successfully!");
      setOtp(new Array(6).fill(""));
      inputRefs.current[0].focus();
    } catch (error) {
      toast.error("Failed to resend OTP");
    } finally {
      setResendLoading(false);
    }
  };

  // Start timer when component mounts
  useEffect(() => {
    setTimer(30);
  }, []);

  // Timer countdown logic
  useEffect(() => {
    if (timer === 0) return;

    const countdown = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          clearInterval(countdown);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(countdown);
  }, [timer]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        mt: 2,
      }}
    >
      {/* OTP Input Fields */}
      <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}>
        {otp.map((digit, index) => (
          <OtpInput
            key={index}
            value={digit}
            placeholder="●"
            inputRef={(el) => (inputRefs.current[index] = el)}
            onChange={(e) => handleChange(index, e)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={index === 0 ? handlePaste : null}
            inputProps={{ maxLength: 1, inputMode: "numeric" }}
            autoFocus={index === 0}
          />
        ))}
      </Box>

      <Box sx={{ textAlign: "left" }}>
        {/* ResendButton Button */}
        <ResendButton
          disabled={resendLoading || timer > 0}
          variant="outlined"
          onClick={handleResend}
          sx={{ mr: 2 }}
        >
          {resendLoading ? (
            <CircularProgress size={24} sx={{ color: "#fff" }} />
          ) : timer > 0 ? (
            `Resend OTP in ${timer}s`
          ) : (
            "Resend OTP"
          )}
        </ResendButton>
        {/* Verify Button */}
        <VerifyButton
          variant="contained"
          disabled={!isOtpComplete || loading}
          onClick={handleOTPVerify}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: "#fff" }} />
          ) : (
            "Verify"
          )}
        </VerifyButton>
      </Box>
    </Box>
  );
};

export default OTPSection;
